import * as React from "react";
import { Link, useNavigate, useSearch } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { CalendarClock, Search } from "lucide-react";
import { Avatar, Boton, cn, EsqueletoTabla, Input, Pill, PuntoNaranja, TituloPagina, Vacio } from "@iavmx/ui";
import { leer } from "../api";
import { Candado, PillsAlerta, Segmentado } from "../componentes";
import { useNombresProducto, useYo } from "../datos";
import { fechaCorta, mxn, relativo } from "../formato";
import { useSesion } from "../sesion";
import { ETAPAS, ETIQUETA_ETAPA, type Etapa, type OportunidadTarjeta, type RespuestaOportunidades } from "../tipos";

function Tarjeta({ o }: { o: OportunidadTarjeta }) {
  const nombreDe = useNombresProducto();
  const ajena = !o.montoVisible;
  const vencida = o.alertas.vencida;
  return (
    <Link
      to="/oportunidades/$id"
      params={{ id: o.id }}
      className={cn(
        "flex min-h-11 flex-col gap-2 border border-linea p-3 transition-colors hover:border-linea-fuerte focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
        ajena ? "bg-fondo-suave" : "bg-fondo",
      )}
    >
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <div className="font-titulo text-[15px] font-semibold leading-tight">{o.cuenta}</div>
          <div className="mt-0.5 truncate font-ui text-xs text-tinta-3">{o.nombre}</div>
        </div>
        <Avatar nombre={o.ejecutivo ?? "?"} className={cn("size-6 text-[10px]", ajena && "opacity-60")} />
      </div>
      {o.productos.length > 0 && <div className="font-ui text-xs text-tinta-3">{o.productos.map(nombreDe).join(" · ")}</div>}
      {ajena ? (
        <Candado texto={`Cotización de ${o.ejecutivo ?? "otro ejecutivo"} · no visible`} />
      ) : (
        o.valor && <div className="font-titulo text-lg font-bold tabular-nums">{mxn(o.valor)}</div>
      )}
      {(o.etapa === "ganado" || o.etapa === "perdido") ? (
        <div className="font-ui text-xs text-tinta-tenue">
          {o.etapa === "perdido" && o.motivoPerdida ? `Motivo: ${o.motivoPerdida}` : `Cerrada ${relativo(o.cerradaEn)}`}
        </div>
      ) : (
        <div className="flex gap-2 border-t border-linea pt-2 font-ui text-xs">
          <CalendarClock className="mt-0.5 size-3.5 shrink-0 text-tinta-tenue" aria-hidden />
          <div className="min-w-0">
            <div className="font-semibold text-tinta-2">{o.proximaAccion}</div>
            <div className={cn(vencida ? "text-rojo" : "text-tinta-tenue")}>
              {fechaCorta(o.proximaAccionFecha)} · {relativo(o.proximaAccionFecha)}
            </div>
          </div>
        </div>
      )}
      {o.propuesta && (
        <Pill tono="azul" className="self-start">
          {o.propuesta.estado === "aceptada"
            ? "Propuesta aceptada"
            : o.propuesta.estado === "cambios_pedidos"
              ? "Pidió cambios"
              : o.propuesta.primeraAperturaEn
                ? `Propuesta abierta ${relativo(o.propuesta.primeraAperturaEn)}`
                : o.propuesta.enviadaEn
                  ? `Sin abrir · enviada ${relativo(o.propuesta.enviadaEn)}`
                  : "Propuesta en borrador"}
        </Pill>
      )}
      <PillsAlerta alertas={o.alertas} etapa={o.etapa} altoValor={o.altoValor} />
    </Link>
  );
}

export function Pipeline() {
  const { api, listo } = useSesion();
  const yo = useYo();
  const navegar = useNavigate();
  const busqueda = useSearch({ from: "/" });
  const mias = busqueda.mias ?? false;
  const [q, setQ] = React.useState(busqueda.q ?? "");
  const [etapaMovil, setEtapaMovil] = React.useState<Etapa>("lead");
  const lista = useQuery({
    queryKey: ["oportunidades", mias],
    queryFn: () => api.api.oportunidades.$get({ query: mias ? { mias: "1" } : {} }).then((r) => leer<RespuestaOportunidades>(r)),
    enabled: listo,
  });

  const filtro = q.trim().toLowerCase();
  const todas = (lista.data?.oportunidades ?? []).filter((o) => !filtro || `${o.cuenta} ${o.nombre} ${o.ejecutivo ?? ""}`.toLowerCase().includes(filtro));
  const mesActual = new Date().toISOString().slice(0, 7);
  const porEtapa = (e: Etapa) => todas.filter((o) => o.etapa === e && (e !== "ganado" && e !== "perdido" ? true : (o.cerradaEn ?? "").slice(0, 7) === mesActual));
  const suma = (e: Etapa) => porEtapa(e).reduce((s, o) => s + (o.montoVisible ? Number(o.valor ?? 0) : 0), 0);
  const r = lista.data?.resumen;
  const mesEtiqueta = new Intl.DateTimeFormat("es-MX", { month: "long", year: "numeric" }).format(new Date());
  const gerencia = yo.data ? ["admin", "direccion", "gerencia"].includes(yo.data.rol) : false;

  return (
    <>
      <TituloPagina
        descripcion={`${mesEtiqueta.charAt(0).toUpperCase() + mesEtiqueta.slice(1)} · ${r ? `${r.abiertas} oportunidades abiertas` : "…"}`}
        accion={
          <div className="flex flex-wrap items-center gap-2">
            <Segmentado
              etiqueta="Ámbito"
              valor={mias ? "mias" : "todo"}
              opciones={[
                { valor: "todo", etiqueta: "Todo el equipo" },
                { valor: "mias", etiqueta: "Solo mías" },
              ]}
              onCambio={(v) => void navegar({ to: "/", search: { mias: v === "mias" || undefined, q: q || undefined } })}
            />
            <label className="relative">
              <span className="sr-only">Buscar cuenta u oportunidad</span>
              <Search className="pointer-events-none absolute left-2.5 top-1/2 size-4 -translate-y-1/2 text-tinta-tenue" aria-hidden />
              <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Buscar cuenta o contacto" className="h-8 w-56 pl-8 text-base sm:text-sm" />
            </label>
            <Boton variante="acento" tamano="sm" asChild>
              <Link to="/cuentas/nueva">Nueva cuenta</Link>
            </Boton>
          </div>
        }
      >
        Pipeline
        <PuntoNaranja />
      </TituloPagina>

      {r && (
        <div className="mb-4 flex flex-wrap items-center gap-2">
          {r.detenidas > 0 && <Pill tono="tinta">{r.detenidas} detenida{r.detenidas > 1 ? "s" : ""}</Pill>}
          {r.vencidas > 0 && <Pill tono="rojo">{r.vencidas} con próxima acción vencida</Pill>}
          {r.escaladas > 0 && <Pill tono="tinta">{r.escaladas} escalada{r.escaladas > 1 ? "s" : ""} a dirección</Pill>}
          {r.detenidas === 0 && r.vencidas === 0 && <Pill tono="verde">Todo con próxima acción vigente</Pill>}
          <span className="ml-auto font-ui text-xs text-tinta-tenue">
            {gerencia ? "Gerencia: ves todo el equipo con montos." : "Ves la prospección de todo el equipo; las cotizaciones de otros ejecutivos no se muestran."}
          </span>
        </div>
      )}

      {lista.isLoading && <EsqueletoTabla filas={6} columnas={6} />}
      {lista.error && <Vacio titulo="No se pudo cargar el pipeline" texto={lista.error.message} />}
      {lista.data && todas.length === 0 && (
        <Vacio
          titulo={filtro ? "Sin coincidencias" : "Sin oportunidades todavía"}
          texto={filtro ? `Nada coincide con «${q}».` : "Da de alta una cuenta y crea su primera oportunidad con próxima acción."}
          accion={
            !filtro && (
              <Boton variante="acento" asChild>
                <Link to="/cuentas/nueva">Nueva cuenta</Link>
              </Boton>
            )
          }
        />
      )}

      {lista.data && todas.length > 0 && (
        <>
          {/* Celular: una etapa a la vez, con pills desplazables. */}
          <div className="md:hidden">
            <div role="tablist" aria-label="Etapas" className="-mx-6 flex gap-2 overflow-x-auto px-6 pb-3 [scrollbar-width:none]">
              {ETAPAS.map((e) => (
                <button
                  key={e}
                  type="button"
                  role="tab"
                  aria-selected={etapaMovil === e}
                  onClick={() => setEtapaMovil(e)}
                  className={cn(
                    "h-8 shrink-0 rounded-full border px-3 font-ui text-xs font-semibold",
                    etapaMovil === e ? "border-tinta bg-tinta text-tinta-inversa" : "border-linea-fuerte text-tinta-2",
                  )}
                >
                  {ETIQUETA_ETAPA[e]} · {porEtapa(e).length}
                </button>
              ))}
            </div>
            <div className="flex flex-col gap-2" role="tabpanel">
              {porEtapa(etapaMovil).map((o) => (
                <Tarjeta key={o.id} o={o} />
              ))}
              {porEtapa(etapaMovil).length === 0 && <p className="py-6 text-center font-ui text-sm text-tinta-tenue">Nada en {ETIQUETA_ETAPA[etapaMovil].toLowerCase()}.</p>}
            </div>
          </div>

          {/* Escritorio: seis columnas; Propuesta y Negociación más anchas y con fondo panel. */}
          <div className="hidden overflow-x-auto md:block">
            <div className="grid min-w-[1120px] grid-cols-[1fr_1fr_1.25fr_1.25fr_1fr_.9fr] items-start gap-4">
              {ETAPAS.map((e) => {
                const enfasis = e === "propuesta" || e === "negociacion";
                const items = porEtapa(e);
                const total = suma(e);
                return (
                  <section key={e} aria-label={ETIQUETA_ETAPA[e]} className={cn("flex min-w-0 flex-col gap-2.5", enfasis && "-my-3 bg-fondo-suave p-3")}>
                    <header className="flex items-baseline justify-between border-b border-linea-fuerte pb-2">
                      <h2 className="font-titulo text-[15px] font-bold" title={e === "ganado" || e === "perdido" ? "Cerradas este mes" : undefined}>
                        {ETIQUETA_ETAPA[e]}
                        <span className="ml-1.5 font-ui text-xs font-semibold text-tinta-tenue">{items.length}</span>
                      </h2>
                      <span className="font-titulo text-xs font-bold tabular-nums text-tinta-3">{total > 0 ? mxn(total) : "—"}</span>
                    </header>
                    {items.map((o) => (
                      <Tarjeta key={o.id} o={o} />
                    ))}
                    {items.length === 0 && <p className="py-4 text-center font-ui text-xs text-tinta-tenue">Vacío</p>}
                  </section>
                );
              })}
            </div>
          </div>
        </>
      )}
    </>
  );
}
